import { FC, PropsWithChildren, useEffect, useMemo, memo } from 'react';
import styled from 'styled-components';
import { Button, Flex } from 'antd';
import { Tooltip } from '../../../components/Tooltip';
import { CellContext, Row } from '@tanstack/react-table';
import { LEVEL_TABULATION_WIDTH_PX } from '../../../tanstacktable';
import * as Icon from 'react-feather';
import {
  colorBgLayout,
  colorTableOperationIcon,
} from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './ExpandTableRowButton.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'expand-table-row-button'
);
addTranslations(translations);

export type ExpandTableRowButtonProps = PropsWithChildren<
  CellContext<any, any>
>;

export const ExpandTableRowButton: FC<ExpandTableRowButtonProps> = memo(
  (props) => {
    const { row, children } = props;
    const { t } = useTranslation();
    const canExpand = row.getCanExpand();

    const button = (
      <StyledButton
        id={`expand-row-${row.index}`}
        type="text"
        shape="circle"
        size="small"
        onClick={row.getToggleExpandedHandler()}
        disabled={!canExpand}
        icon={
          !canExpand ? (
            <Spacer />
          ) : row.getIsExpanded() ? (
            <Icon.ChevronDown size={22} color={colorTableOperationIcon} />
          ) : (
            <Icon.ChevronRight size={22} color={colorTableOperationIcon} />
          )
        }
      />
    );

    return (
      <>
        {row.index === 0 && !row.parentId && (
          <AutoAdjustColumnWidth {...props} />
        )}
        {row.depth > 0 && (
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${row.depth * LEVEL_TABULATION_WIDTH_PX}px`,
              backgroundColor: colorBgLayout,
            }}
          />
        )}
        <Wrapper gap="small" align="start" $depth={row.depth}>
          {canExpand ? (
            button
          ) : (
            <Tooltip title={t('no-children')}>{button}</Tooltip>
          )}
          {children}
        </Wrapper>
      </>
    );
  }
);

const StyledButton = styled(Button)`
  margin-block: -1px;
  vertical-align: middle;
`;

const Spacer = styled.div`
  width: 32px;
  height: 32px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 100%;
    width: 2px;
    height: 2px;
    border: 2px solid ${colorTableOperationIcon};
  }
`;

const Wrapper = styled(Flex)<{ $depth: number }>`
  padding-left: ${({ $depth }) => $depth * LEVEL_TABULATION_WIDTH_PX}px;
`;

export const AutoAdjustColumnWidth: FC<ExpandTableRowButtonProps> = (props) => {
  const { column, table } = props;
  const tableName = table.options.name;
  const { rows } = table.getRowModel();
  const maxDepth = useMemo(() => getMaxDepth(rows), [rows]);
  const columnIndex = useMemo(
    () => table.getAllColumns().findIndex(({ id }) => id === column.id),
    [table, column.id]
  );
  const { width, minWidth, maxWidth } = column.columnDef.meta || {};

  useEffect(() => {
    if (maxDepth) {
      const selector = `#${tableName} colgroup col:nth-child(${
        columnIndex + 1
      })`;
      const cols: NodeListOf<HTMLElement> = document.querySelectorAll(selector);

      if (!cols.length) {
        console.error(
          `Unable to auto-adjust column width: could not find colgroup col element(s) by selector ${selector}`
        );
        return;
      }

      const extraWidth = maxDepth * LEVEL_TABULATION_WIDTH_PX;

      adjustColWidth(cols, 'width', width, extraWidth);
      adjustColWidth(cols, 'minWidth', minWidth, extraWidth);
      adjustColWidth(cols, 'maxWidth', maxWidth, extraWidth);
    }
  }, [maxDepth, columnIndex, tableName, column, width, minWidth, maxWidth]);

  return null;
};

const getMaxDepth = (rows: Row<any>[]): number =>
  rows.reduce(
    (count, row) =>
      Math.max(count, row.subRows?.length ? getMaxDepth(row.subRows) + 1 : 0),
    0
  );

const adjustColWidth = (
  cols: NodeListOf<HTMLElement>,
  prop: 'width' | 'minWidth' | 'maxWidth',
  configuredValue: string | number | undefined,
  extraWidth: number
) => {
  if (configuredValue) {
    cols.forEach(
      (col) =>
        (col.style[prop] =
          typeof configuredValue === 'number'
            ? `${configuredValue + extraWidth}px`
            : `calc(${configuredValue} + ${extraWidth}px)`)
    );
  }
};
